import {
  BUILDING_SITE_TABLE_NAME,
  CreateBuildingSiteInput,
  CreateBuildingSiteMutationVariables as CreateMutationVariables,
  DeleteBuildingSiteMutationVariables as DeleteMutationVariables,
  cleanInputCreate,
  cleanInputUpdate,
  createBuildingSite as createMutation,
  deleteBuildingSite as deleteMutation,
  getBuildingSite as getQuery,
  getReadId,
  getTableClientId,
  isNilOrEmpty,
  resolveOneToMany,
  syncBuildingSites as syncQuery,
  updateBuildingSite as updateMutation,
  BuildingSite,
  OnCreateBuildingSiteSubscription,
  OnUpdateBuildingSiteSubscription,
  OnDeleteBuildingSiteSubscription,
  onDeleteBuildingSite,
  onUpdateBuildingSite,
  onCreateBuildingSite,
  UpdateBuildingSiteInput,
  removeNestedFieldsExcept,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
} from '@rentguru/commons-utils';
import {
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { createContext, Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useBuildings } from './BuildingsContext';
import { useUser } from './UserContext';
import { canUserAccessEntity } from './utils';
import { usePermissions } from './utils/PermissionsContext';

const FIELDS_TO_REMOVE = ['__typename', '_lastChangedAt'];

type BuildingSiteWithoutBuildings = Omit<BuildingSite, 'buildings'>;

type ActionBuildingSite =
  | {
      type: 'SHOULD_FETCH_BUILDING_SITE' | 'IS_FETCHING_BUILDING_SITE';
    }
  | {
      type: 'ADD_BUILDING_SITE';
      payload: { buildingSite: BuildingSiteWithoutBuildings };
    }
  | {
      type: 'FETCHED_BUILDING_SITE';
      payload: { buildingSites: BuildingSiteWithoutBuildings[] };
    }
  | {
      type: 'UPDATE_BUILDING_SITE';
      payload: { buildingSite: BuildingSiteWithoutBuildings };
    }
  | {
      type: 'DELETE_BUILDING_SITE';
      payload: { id: string };
    }
  | {
      type: 'FETCH_FAILED_BUILDING_SITE';
    };

interface BuildingSiteState {
  buildingSites: BuildingSite[];
  buildingSitesWithoutBuildings: BuildingSiteWithoutBuildings[];
  shouldFetchBuildingSites: boolean;
  buildingSitesLoading: boolean;
}

const buildingSiteInitialState: BuildingSiteState = {
  buildingSites: [],
  buildingSitesWithoutBuildings: [],
  shouldFetchBuildingSites: true,
  buildingSitesLoading: false,
};

const removeUnwantedEntitiesFromBuildingSites = (query: string) =>
  removeNestedFieldsExcept(
    query,
    DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
    ['buildings'],
    undefined,
    FIELDS_TO_REMOVE
  );
export interface BuildingSitesContext extends Omit<BuildingSiteState, 'buildingSitesWithoutBuildings'> {
  getBuildingSite: (id: string) => BuildingSite | undefined;
  createBuildingSite: (
    input: Omit<CreateBuildingSiteInput, 'clientId' | 'readId'>
  ) => Promise<BuildingSite | undefined>;
  updateBuildingSite: (updates: UpdateBuildingSiteInput) => Promise<BuildingSite | undefined>;
  deleteBuildingSite: (id: string) => Promise<void>;
  setFetchBuildingSites: () => void;
}

const buildingSiteReducer = (state: BuildingSiteState, action: ActionBuildingSite): BuildingSiteState => {
  switch (action.type) {
    case 'SHOULD_FETCH_BUILDING_SITE':
      if (state.buildingSitesLoading || state.shouldFetchBuildingSites) {
        return state;
      }
      return {
        ...state,
        shouldFetchBuildingSites: true,
      };
    case 'IS_FETCHING_BUILDING_SITE':
      if (state.buildingSitesLoading) {
        return state;
      }
      return {
        ...state,
        buildingSitesLoading: true,
      };

    case 'FETCHED_BUILDING_SITE':
      return {
        ...state,
        buildingSitesWithoutBuildings: action.payload.buildingSites,
        buildingSitesLoading: false,
        shouldFetchBuildingSites: false,
      };
    case 'FETCH_FAILED_BUILDING_SITE':
      return {
        ...state,
        buildingSitesLoading: false,
        shouldFetchBuildingSites: false,
      };
    case 'ADD_BUILDING_SITE':
      if (state.buildingSitesWithoutBuildings.find((object) => object.id === action.payload.buildingSite.id)) {
        return state;
      }
      return {
        ...state,
        buildingSitesWithoutBuildings: [...state.buildingSitesWithoutBuildings, action.payload.buildingSite],
      };
    case 'UPDATE_BUILDING_SITE':
      if (isNilOrEmpty(state.buildingSitesWithoutBuildings)) {
        return state;
      }
      const currentObject = state.buildingSitesWithoutBuildings.find(
        (object) => object.id === action.payload.buildingSite.id
      );
      if (!currentObject) {
        return {
          ...state,
          buildingSitesWithoutBuildings: [...state.buildingSitesWithoutBuildings, action.payload.buildingSite],
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.buildingSite)) {
        return state;
      }

      return {
        ...state,
        buildingSitesWithoutBuildings: state.buildingSitesWithoutBuildings.map((currentBuildingSite) => {
          if (currentBuildingSite.id === action.payload.buildingSite.id) {
            return action.payload.buildingSite;
          }
          return currentBuildingSite;
        }),
      };
    case 'DELETE_BUILDING_SITE':
      if (isNilOrEmpty(state.buildingSitesWithoutBuildings)) {
        return state;
      }
      return {
        ...state,
        buildingSitesWithoutBuildings: state.buildingSitesWithoutBuildings.filter(
          (object) => object.id !== action.payload.id
        ),
      };
    default:
      return state;
  }
};

const fetchBuildingSites = async (
  clientId: string,
  additionalFilter?: object
): Promise<BuildingSiteWithoutBuildings[]> =>
  await list<BuildingSiteWithoutBuildings>(
    removeUnwantedEntitiesFromBuildingSites(syncQuery),
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, BUILDING_SITE_TABLE_NAME),
    additionalFilter
  );

export const BuildingSitesContext = createContext<BuildingSitesContext | null>(null);

export const BuildingSiteContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const { buildings, buildingsLoading } = useBuildings();
  const { buildingsUnitsDetailsDelete } = usePermissions();
  const { clientId, isOwner, userId, groupIds: teamIds } = useUser();
  const [state, dispatch] = useReducer<Reducer<BuildingSiteState, ActionBuildingSite>>(
    buildingSiteReducer,
    buildingSiteInitialState
  );

  const loading = buildingsLoading;

  useEffect(() => {
    const fetchAndSet = async () => {
      if (!clientId) return;
      dispatch({ type: 'IS_FETCHING_BUILDING_SITE' });
      try {
        const result = await fetchBuildingSites(clientId);
        dispatch({ type: 'FETCHED_BUILDING_SITE', payload: { buildingSites: result } });
      } catch (error) {
        console.error('[buildingSites] fetching building sites failed', error);
        dispatch({ type: 'FETCH_FAILED_BUILDING_SITE' });
      }
    };
    if (state.shouldFetchBuildingSites) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchBuildingSites]);

  useSubscriptions<
    OnCreateBuildingSiteSubscription,
    OnUpdateBuildingSiteSubscription,
    OnDeleteBuildingSiteSubscription
  >(
    onCreateBuildingSite,
    onUpdateBuildingSite,
    onDeleteBuildingSite,
    (data) => {
      dispatch({
        type: 'ADD_BUILDING_SITE',
        payload: { buildingSite: data.onCreateBuildingSite as BuildingSite },
      });
    },
    (data) => {
      dispatch({
        type: 'UPDATE_BUILDING_SITE',
        payload: { buildingSite: data.onUpdateBuildingSite as BuildingSite },
      });
    },
    (data) => {
      const { id } = data.onDeleteBuildingSite as BuildingSite;
      dispatch({
        type: 'DELETE_BUILDING_SITE',
        payload: { id },
      });
    }
  );

  const buildingSites = useMemo<BuildingSite[]>(() => {
    if (loading) {
      return [];
    }

    return state.buildingSitesWithoutBuildings.reduce<BuildingSite[]>((acc, buildingSite) => {
      if (canUserAccessEntity(isOwner, buildingSite.readers, userId, teamIds, buildingSite.teams)) {
        acc.push({
          ...buildingSite,
          buildings: resolveOneToMany(buildingSite.id, buildings, 'buildingSiteId'),
        });
      }
      return acc;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, state.buildingSitesWithoutBuildings, isOwner, userId, teamIds, buildings]);

  const getBuildingSite = (id: string) => buildingSites.find((buildingSite) => buildingSite.id === id);

  const createBuildingSite = async (
    input: Omit<CreateBuildingSiteInput, 'clientId' | 'readId'>
  ): Promise<BuildingSite | undefined> => {
    if (!clientId) {
      return undefined;
    }
    const cleanedInput = cleanInputCreate(input) as CreateBuildingSiteInput;
    const teams = isNilOrEmpty(cleanedInput.teams) ? (teamIds ?? []) : cleanedInput.teams;
    const building = await mutation<BuildingSiteWithoutBuildings, CreateMutationVariables>(createMutation, {
      input: {
        ...cleanedInput,
        teams,
        clientId: getTableClientId(clientId, BUILDING_SITE_TABLE_NAME),
        readId: getReadId(clientId, BUILDING_SITE_TABLE_NAME),
      },
    });
    dispatch({ type: 'ADD_BUILDING_SITE', payload: { buildingSite: building } });
    return building;
  };

  const updateBuildingSite = async (updates: UpdateBuildingSiteInput) => {
    const original = getBuildingSite(updates.id);
    if (!original) {
      return undefined;
    }
    const result = await mutation<BuildingSite>(updateMutation, {
      input: { ...cleanInputUpdate(updates, false), _version: original._version },
    });
    dispatch({ type: 'UPDATE_BUILDING_SITE', payload: { buildingSite: result } });
    return result;
  };

  const deleteBuildingSite = async (id: string) => {
    if (!buildingsUnitsDetailsDelete) {
      return;
    }

    await deleteEntityWithFetchBefore<Pick<BuildingSite, 'id'>, DeleteMutationVariables>(
      { id },
      getQuery,
      deleteMutation
    );
    dispatch({ type: 'DELETE_BUILDING_SITE', payload: { id } });
  };

  const values = useMemo(
    () => ({
      buildingSites,
      buildingSitesLoading: state.buildingSitesLoading,
      shouldFetchBuildingSites: state.shouldFetchBuildingSites,
      getBuildingSite,
      createBuildingSite,
      updateBuildingSite,
      deleteBuildingSite,
      setFetchBuildingSites: () => dispatch({ type: 'SHOULD_FETCH_BUILDING_SITE' }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildings, loading, state]
  );

  return <BuildingSitesContext.Provider value={values}>{children}</BuildingSitesContext.Provider>;
};

export const useBuildingSitesContext = (): BuildingSitesContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<BuildingSitesContext | null>(BuildingSitesContext);

  if (isNil(context)) {
    throw new Error('`useBuildingSitesContext` hook must be used within a `BuildingSiteContextProvider` component');
  }

  useEffect(() => {
    if (!firstRender.current && isNil(context.buildingSites)) {
      context.setFetchBuildingSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context as BuildingSitesContext;
};
